import React, { useState, useEffect } from 'react';
import axios from './axiosConfig';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { useConfirm } from '../context/ConfirmContext';

function UserManagement() {
  const { user: currentUser } = useAuth();
  const { confirm } = useConfirm();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'Usuario',
    is_active: true
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [resettingUserId, setResettingUserId] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  // Función helper para formatear errores de validación del backend
  const formatErrorMessage = (error) => {
    if (!error.response?.data) return error.message || 'Error desconocido';
    
    const errorData = error.response.data;
    
    // Si detail es un array de errores de validación de Pydantic
    if (Array.isArray(errorData.detail)) {
      return errorData.detail.map(err => {
        // Extraer el campo del array loc (ej: ["body", "password"] -> "password")
        const field = err.loc ? err.loc[err.loc.length - 1] : 'Campo';
        const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
        return `${fieldName}: ${err.msg}`;
      }).join('. ');
    }
    
    // Si detail es un string simple
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }
    
    // Si detail es un objeto con msg
    if (errorData.detail?.msg) {
      return errorData.detail.msg;
    }
    
    return error.message || 'Error al procesar la solicitud';
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users/');
      setUsers(response.data);
    } catch (err) {
      setError('Error al cargar usuarios: ' + formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/users/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateUser = () => {
    setIsEditing(false);
    setSelectedUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'Usuario',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setIsEditing(true);
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      if (isEditing && selectedUser) {
        // Actualizar usuario
        const updateData = {
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`/api/users/${selectedUser.id}`, updateData);
        setSuccessMessage('Usuario actualizado correctamente');
      } else {
        setCreatingUser(true);
        const { data } = await axios.post('/api/users/', {
          username: formData.username,
          email: formData.email,
          role: formData.role,
        });
        if (data.email_sent) {
          setSuccessMessage(
            `Se envió un correo a ${formData.email} con la contraseña temporal. En el primer acceso deberá cambiarla.`
          );
        } else if (data.dev_credentials?.password) {
          setSuccessMessage(
            `Usuario creado (sin SMTP configurado). Contraseña temporal: ${data.dev_credentials.password}. Compártela por un canal seguro.`
          );
        } else {
          setSuccessMessage(data.message || 'Usuario creado.');
        }
      }
      
      fetchUsers();
      fetchStats();
      setShowModal(false);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'Usuario',
        is_active: true
      });
    } catch (err) {
      setError(formatErrorMessage(err));
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const accepted = await confirm({
      title: 'Eliminar usuario',
      message: '¿Estás seguro de eliminar este usuario?',
      details: 'El usuario perderá acceso al sistema y su cuenta será eliminada.',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      variation: 'danger',
      badge: 'Eliminar',
      icon: 'person_remove',
    });
    if (!accepted) return;

    try {
      await axios.delete(`/api/users/${userId}`);
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await axios.patch(`/api/users/${userId}/toggle-status`);
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(formatErrorMessage(err));
    }
  };

  const handleResetPassword = async (user) => {
    const accepted = await confirm({
      title: 'Resetear contraseña',
      message: `Se generará una nueva contraseña temporal para ${user.username}.`,
      details: 'El usuario recibirá la nueva contraseña en su correo y quedará obligado a cambiarla en el siguiente ingreso.',
      confirmText: 'Sí, resetear',
      cancelText: 'Cancelar',
      variation: 'warning',
      badge: 'Seguridad',
      icon: 'lock_reset',
    });
    if (!accepted) return;

    try {
      setResettingUserId(user.id);
      setError(null);
      setSuccessMessage(null);
      const { data } = await axios.post(`/api/users/${user.id}/reset-password`);
      if (data.email_sent) {
        setSuccessMessage(`Se envió una nueva contraseña temporal a ${user.email}. El usuario deberá cambiarla al ingresar.`);
      } else if (data.dev_credentials?.password) {
        setSuccessMessage(
          `Reset realizado sin SMTP configurado. Contraseña temporal para ${user.username}: ${data.dev_credentials.password}. Compártela por un canal seguro.`
        );
      } else {
        setSuccessMessage(data.message || 'Contraseña reseteada correctamente.');
      }
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(formatErrorMessage(err));
    } finally {
      setResettingUserId(null);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'SuperAdmin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'Administrador':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SuperAdmin':
        return 'shield';
      case 'Administrador':
        return 'admin_panel_settings';
      default:
        return 'person';
    }
  };

  const pendingPasswordChangeCount = users.filter((user) => user.requiere_cambio_password).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#101922] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <motion.header 
        className="bg-surface-light dark:bg-surface-dark shadow-sm border-b border-border-light dark:border-border-dark z-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h2 className="text-xl font-bold leading-7 text-slate-900 dark:text-white sm:truncate">Gestión de Usuarios</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Administra todos los usuarios del sistema</p>
            </div>
            <button
              onClick={handleCreateUser}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-md transition-all flex items-center gap-2"
            >
              <span className="material-icons-round text-sm">person_add</span>
              Crear Usuario
            </button>
          </div>
        </div>
      </motion.header>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-8">
        
        {/* Sección de creación de usuario movida al header */}
        <div className="mb-8">

          {/* Estadísticas */}
          {stats && (
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center justify-between mb-6 px-1">
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Estadísticas de Usuarios</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                <div className="bg-white dark:bg-[#182635] rounded-xl p-6 border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Total Usuarios</p>
                      <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <span className="material-icons-round text-blue-600 dark:text-blue-400">groups</span>
                    </div>
                  </div>
                </div>

              <div className="bg-white dark:bg-[#182635] rounded-xl p-6 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Activos</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.active}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <span className="material-icons-round text-green-600 dark:text-green-400">check_circle</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-[#182635] rounded-xl p-6 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Inactivos</p>
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">{stats.inactive}</p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                    <span className="material-icons-round text-red-600 dark:text-red-400">block</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-[#182635] rounded-xl p-6 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">SuperAdmins</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.by_role?.SuperAdmin || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <span className="material-icons-round text-purple-600 dark:text-purple-400">shield</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-[#182635] rounded-xl p-6 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Cambio pendiente</p>
                    <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">{pendingPasswordChangeCount}</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                    <span className="material-icons-round text-amber-600 dark:text-amber-400">vpn_key</span>
                  </div>
                </div>
              </div>
            </div>
            </motion.section>
          )}
        </div>

        {/* Success Alert */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center justify-between">
            <div className="flex items-center">
              <span className="material-icons-round text-green-500 mr-3">check_circle</span>
              <span className="text-green-700 dark:text-green-300">{successMessage}</span>
            </div>
            <button onClick={() => setSuccessMessage(null)} className="text-green-500 hover:text-green-700">
              <span className="material-icons-round">close</span>
            </button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center justify-between">
            <div className="flex items-center">
              <span className="material-icons-round text-red-500 mr-3">error</span>
              <span className="text-red-700 dark:text-red-300">{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <span className="material-icons-round">close</span>
            </button>
          </div>
        )}

        {/* Vista móvil */}
        <div className="grid grid-cols-1 gap-4 lg:hidden">
          {users.map((user) => (
            <div
              key={user.id}
              className="bg-white dark:bg-[#182635] rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center min-w-0">
                  <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${
                    user.role === 'SuperAdmin' ? 'from-purple-500 to-pink-500' :
                    user.role === 'Administrador' ? 'from-blue-500 to-cyan-500' :
                    'from-green-500 to-teal-500'
                  } flex items-center justify-center text-white font-bold mr-3 shrink-0`}>
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-white truncate">{user.username}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 break-all">{user.email}</div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">ID: {user.id}</div>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${getRoleBadgeColor(user.role)}`}>
                  <span className="material-icons-round text-[14px]">{getRoleIcon(user.role)}</span>
                  {user.role}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Estado</div>
                  <div className="mt-2">
                    {user.is_active ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs font-semibold">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-xs font-semibold">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        Inactivo
                      </span>
                    )}
                  </div>
                </div>

                <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Acceso</div>
                  <div className="mt-2">
                    {user.requiere_cambio_password ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs font-semibold">
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                        Debe cambiar
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300 rounded-full text-xs font-semibold">
                        <span className="w-2 h-2 bg-sky-500 rounded-full"></span>
                        Ya ingresó
                      </span>
                    )}
                  </div>
                </div>

                <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Creado</div>
                  <div className="mt-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                    {new Date(user.created_at).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  onClick={() => handleEditUser(user)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300"
                >
                  <span className="material-icons-round text-[18px]">edit</span>
                  Editar
                </button>
                <button
                  onClick={() => handleToggleStatus(user.id)}
                  disabled={user.id === currentUser?.id}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    user.id === currentUser?.id
                      ? 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600 cursor-not-allowed'
                      : user.is_active
                        ? 'bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-300'
                        : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-300'
                  }`}
                >
                  <span className="material-icons-round text-[18px]">{user.is_active ? 'block' : 'check_circle'}</span>
                  {user.is_active ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  onClick={() => handleResetPassword(user)}
                  disabled={resettingUserId === user.id}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    resettingUserId === user.id
                      ? 'bg-amber-50 text-amber-500 dark:bg-amber-900/20 dark:text-amber-400 cursor-wait'
                      : 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300'
                  }`}
                >
                  <span className={`material-icons-round text-[18px] ${resettingUserId === user.id ? 'animate-spin' : ''}`}>
                    {resettingUserId === user.id ? 'sync' : 'lock_reset'}
                  </span>
                  Resetear
                </button>
                <button
                  onClick={() => handleDeleteUser(user.id)}
                  disabled={user.id === currentUser?.id}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    user.id === currentUser?.id
                      ? 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600 cursor-not-allowed'
                      : 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300'
                  }`}
                >
                  <span className="material-icons-round text-[18px]">delete</span>
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Tabla de Usuarios */}
        <div className="hidden lg:block bg-white dark:bg-[#182635] rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Acceso
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Creado
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${
                        user.role === 'SuperAdmin' ? 'from-purple-500 to-pink-500' :
                        user.role === 'Administrador' ? 'from-blue-500 to-cyan-500' :
                        'from-green-500 to-teal-500'
                      } flex items-center justify-center text-white font-bold mr-3`}>
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900 dark:text-white">{user.username}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">ID: {user.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(user.role)}`}>
                      <span className="material-icons-round text-[14px]">{getRoleIcon(user.role)}</span>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {user.is_active ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs font-semibold">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-xs font-semibold">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        Inactivo
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {user.requiere_cambio_password ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs font-semibold">
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                        Debe cambiar
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300 rounded-full text-xs font-semibold">
                        <span className="w-2 h-2 bg-sky-500 rounded-full"></span>
                        Ya ingresó
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-300 text-sm">
                    {new Date(user.created_at).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Editar usuario"
                      >
                        <span className="material-icons-round text-[20px]">edit</span>
                      </button>
                      <button
                        onClick={() => handleToggleStatus(user.id)}
                        disabled={user.id === currentUser?.id}
                        className={`p-2 rounded-lg transition-colors ${
                          user.id === currentUser?.id
                            ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                            : user.is_active
                            ? 'text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                            : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                        }`}
                        title={user.is_active ? 'Desactivar' : 'Activar'}
                      >
                        <span className="material-icons-round text-[20px]">
                          {user.is_active ? 'block' : 'check_circle'}
                        </span>
                      </button>
                      <button
                        onClick={() => handleResetPassword(user)}
                        disabled={resettingUserId === user.id}
                        className={`p-2 rounded-lg transition-colors ${
                          resettingUserId === user.id
                            ? 'text-amber-400 dark:text-amber-500 cursor-wait'
                            : 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                        }`}
                        title="Resetear contraseña"
                      >
                        <span className={`material-icons-round text-[20px] ${resettingUserId === user.id ? 'animate-spin' : ''}`}>
                          {resettingUserId === user.id ? 'sync' : 'lock_reset'}
                        </span>
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={user.id === currentUser?.id}
                        className={`p-2 rounded-lg transition-colors ${
                          user.id === currentUser?.id
                            ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                            : 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                        title="Eliminar usuario"
                      >
                        <span className="material-icons-round text-[20px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal de Crear/Editar Usuario */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}></div>
              <div className="relative bg-white dark:bg-[#182635] rounded-2xl shadow-2xl max-w-md w-full">
                {/* Modal Header */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <span className="material-icons-round text-primary">{isEditing ? 'edit' : 'person_add'}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                        {isEditing ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {isEditing ? 'Modifica los datos del usuario' : 'Completa los datos para registrar'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Nombre de usuario *
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      disabled={isEditing}
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-900 disabled:cursor-not-allowed"
                      placeholder="usuario123"
                      required={!isEditing}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Correo electrónico *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="usuario@ejemplo.com"
                      required
                    />
                  </div>

                  {/* Campo de contraseña solo para editar */}
                  {isEditing ? (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Nueva Contraseña (dejar vacío para no cambiar)
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="••••••••"
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Mínimo 8 caracteres, incluir mayúscula y número
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-start gap-3">
                        <span className="material-icons-round text-blue-500 mt-0.5">mail</span>
                        <div>
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            Contraseña por correo
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                            El sistema generará una contraseña temporal segura y la enviará al correo indicado.
                            El usuario deberá cambiarla en su primer inicio de sesión.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Rol *
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    >
                      <option value="Usuario">Usuario</option>
                      <option value="Administrador">Administrador</option>
                      <option value="SuperAdmin">SuperAdmin</option>
                    </select>
                  </div>

                  {isEditing && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="rounded border-slate-300 dark:border-slate-600 bg-transparent text-primary focus:ring-0 focus:ring-offset-0"
                      />
                      <label htmlFor="is_active" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Usuario activo
                      </label>
                    </div>
                  )}

                  {/* Modal Footer */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      disabled={creatingUser}
                      className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={creatingUser}
                      className="flex-1 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {creatingUser ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Creando...
                        </>
                      ) : (
                        isEditing ? 'Guardar Cambios' : 'Crear Usuario'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </>
  );
}

export default UserManagement;
